import { createContext, useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { API } from '../App';

const ClusterContext = createContext();

export function ClusterProvider({ children }) {
    const [clusters, setClusters] = useState([]);
    const [selectedCluster, setSelectedCluster] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchClusters = useCallback(async () => {
        // No token → don't fire the request. The response interceptor
        // would redirect to /login on the 401, fighting any in-flight
        // navigation. Just wait until login dispatches the event below.
        if (!localStorage.getItem('token')) {
            setLoading(false);
            return;
        }
        try {
            const response = await axios.get(`${API}/clusters`);
            setClusters(response.data || []);
        } catch (error) {
            console.error('Failed to fetch clusters:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchClusters();
        // Login.js dispatches this after writing the token. We re-fetch
        // so the dropdown populates without a page refresh. Logout
        // resets it by reloading.
        const onAuth = () => fetchClusters();
        window.addEventListener('optops-auth-changed', onAuth);
        return () => window.removeEventListener('optops-auth-changed', onAuth);
    }, [fetchClusters]);

    // Once clusters arrive, default to the first one — aggregate "All
    // clusters" queries are slow on the backend, and operators almost
    // always want a specific cluster anyway. They can switch back via
    // the dropdown.
    useEffect(() => {
        if (!selectedCluster && clusters.length > 0) {
            setSelectedCluster(clusters[0]);
        }
    }, [clusters, selectedCluster]);

    const value = {
        clusters,
        selectedCluster,
        setSelectedCluster,
        loading,
        refreshClusters: fetchClusters
    };

    return (
        <ClusterContext.Provider value={value}>
            {children}
        </ClusterContext.Provider>
    );
}

export function useCluster() {
    const context = useContext(ClusterContext);
    if (!context) {
        throw new Error('useCluster must be used within a ClusterProvider');
    }
    return context;
}

export default ClusterContext;
